import { ButtonColor, Checkbox, Modal, ModalType } from '@code/innovance-react-ui-kit';
import React, { ChangeEvent, FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  CardObservation,
  Document,
  DocumentStorage,
  PosActivation,
} from 'src/front/api/apiInstance';

import {
  CardActivationType,
  ModelError,
  ObservationDocumentType,
  ObservationVisibilityResponse,
  UploadDocumentResponse,
  UploadType,
} from 'src/front/api/generated';

import infoIcon from 'src/front/assets/icon_info.svg';

import displayPDF from 'src/front/common/utils/domUtils';

import { isInvalidFileError } from 'src/front/common/utils/errorUtils';

import AsyncButton from 'src/front/components/AsyncButton';

import {
  ButtonListRow,
  Container,
  FormElement,
  FormRow,
  InfoLabel,
  InfoRow,
  Subtitle,
  Title,
  WarningText,
} from 'src/front/components/FormStyles';
import SuccessButtonText from 'src/front/components/SuccessButtonText';

import UploadDocumentForm, {
  UploadDocumentFormData,
} from 'src/front/components/UploadDocumentForm';

import { BREAK_POINTS } from 'src/front/constant';

import { useClearDispatch } from 'src/front/contexts/CardObservationContext';

import styled from 'styled-components';

export const Separator = styled.div`
  display: flex;
  margin: 20px 0;
`;

const BigButton = styled(AsyncButton)`
  width: 600px;
  padding-left: 40px;

  text-align: left;
`;
const ProductTransferButton = styled(BigButton)`
  text-align: center;
`;

const CheckboxWrapper = styled(FormElement)`
  display: flex;
  width: 400px;
  margin-left: 280px;
  align-items: center;

  @media only screen and (max-width: ${BREAK_POINTS.MAX.LARGE_1X}) {
    width: 600px;
    margin-left: 0px;
    margin-top: 5px;
  }
`;

const WarningContainer = styled.div`
  display: flex;
`;

const WarningIcon = styled.img`
  padding-right: 5px;
`;

interface Props {
  goToPreviousStep: () => void;
  cardInfo: ObservationVisibilityResponse;
  setIsCompleted?: (value: boolean) => void;
}

interface ButtonProps {
  buttonLabel: string;
  observationDocumentType: ObservationDocumentType;
  visible: boolean;
  clickable?: boolean;
}

const CARD_OBSERVATION_DOCUMENT_TYPE = 'MS';

const OBSERVATION_REQUIRED_DOCUMENT_TYPE = 'INDIVIDUAL_CONDITION';

const CardObservationActivation: FC<Props> = ({ goToPreviousStep, cardInfo, setIsCompleted }) => {
  const { cardNumber, cardStatus, canUploadPanelVisible } = cardInfo;
  const { t } = useTranslation();
  const clearSearchInputs = useClearDispatch();

  const [isPrinted, setIsPrinted] = useState<boolean>(false);
  const [buttonList] = useState<ButtonProps[]>(
    cardInfo.buttonList.filter(({ visible }) => visible)
  );
  const productTransferButton = cardInfo.buttonList.filter(
    ({ observationDocumentType }) =>
      observationDocumentType === ObservationDocumentType.PRODUCTTRANSFER
  )[0];
  const digitalCardButton = cardInfo.buttonList.filter(
    ({ observationDocumentType }) => observationDocumentType === ObservationDocumentType.DIGITALCARD
  )[0];
  const [clickedButtons, setClickedButtons] = useState<string[]>([]);
  const [uploadedDocument, setUploadedDocument] = useState<UploadDocumentResponse>();
  const [innerDocument, setInnerDocument] = useState<UploadDocumentResponse>();
  const [showDocumentUploadForm, setShowDocumentUploadForm] = useState<boolean>(false);
  const [haveDocumentUploadedWarning, setHaveDocumentUploadedWarning] = useState<boolean>(false);
  const [havePrintedWarning, setHavePrintedWarning] = useState<boolean>(false);
  const [haveClickedWarning, setHaveClickedWarning] = useState<boolean>(false);
  const [isApplicationCompleted, setIsApplicationCompleted] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<ModelError>();
  const [isSuccessModalActive, setSuccessModalActive] = useState<boolean>(false);

  const cardActivationButtonVisible = cardInfo.buttonList.find(
    (button) => button.observationDocumentType === ObservationDocumentType.CARDACTIVATION
  )?.visible;
  const individualConditionsVisible = cardInfo.buttonList.find(
    (button) => button.observationDocumentType === ObservationDocumentType.INDIVIDUALCONDITION
  )?.visible;
  const buttonListLength = buttonList.filter(
    (button) => button.observationDocumentType !== ObservationDocumentType.PRODUCTTRANSFER
  ).length;
  const allButtonsClicked =
    clickedButtons.length ===
    (cardActivationButtonVisible ? buttonListLength : buttonListLength + 1);
  const singDocument = () => async (): Promise<void> => {
    try {
      const { data: response } = await CardObservation.signCardObservationCardDocument();
      if (response.result === 'OK') {
        setSuccessModalActive(true);
      }
    } catch (err) {
      // TODO (mertohancan): show error on modal or handle with error boundary
    }
  };
  const handleDownloadApplicationClick = (
    documentType: ObservationDocumentType
  ) => async (): Promise<void> => {
    try {
      if (documentType === ObservationDocumentType.PRODUCTTRANSFER) {
        const {
          data: response,
        } = await CardObservation.signCardObservationProductTransferDocument();
        if (response.result === 'OK') {
          setSuccessModalActive(true);
        }
      } else if (documentType === ObservationDocumentType.DIGITALCARD) {
        clickedButtons.indexOf(documentType) === -1 &&
          setClickedButtons([...clickedButtons, documentType]);
        if (!allButtonsClicked && clickedButtons.indexOf(documentType) !== -1) {
          setHaveDocumentUploadedWarning(false);
          setHaveClickedWarning(true);
        } else {
          const { data: response } = await CardObservation.signCardObservationDigitalCardDocument();
          setHaveClickedWarning(false);
          if (response.result === 'OK') {
            setSuccessModalActive(true);
          }
        }
      } else {
        const { data } = await CardObservation.getCardObservationDocumentId({
          observationDocumentType: documentType,
        });
        if (data.result === 'OK') {
          const { data: response } = await DocumentStorage.getDocument({
            documentId: data.documentId,
          });
          if (response.result === 'OK') {
            clickedButtons.indexOf(documentType) === -1 &&
              setClickedButtons([...clickedButtons, documentType]);
            documentType === ObservationDocumentType.CARDACTIVATION &&
              setShowDocumentUploadForm(true);
            displayPDF(response.documentData);
          }
          if (allButtonsClicked) {
            setHaveClickedWarning(false);
          }
        }
      }
    } catch (err) {
      // TODO (mertohancan): show error on modal or handle with error boundary
    }
  };

  const handlePrinted = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    setIsPrinted(target.checked);
    havePrintedWarning && setHavePrintedWarning(false);
  };

  const handleApplicationSuccessClick = (): void => {
    setIsApplicationCompleted(false);
    clearSearchInputs();
    goToPreviousStep();
    setIsCompleted && setIsCompleted(false);
  };
  const handleModalSuccessClick = (): void => {
    setSuccessModalActive(false);
    if (productTransferButton.visible) {
      setClickedButtons([...clickedButtons, ObservationDocumentType.PRODUCTTRANSFER]);
    }
  };
  const onSubmit = async (formData: UploadDocumentFormData): Promise<void> => {
    try {
      if (innerDocument?.documentId) {
        await Document.deleteDocument({ documentId: innerDocument?.documentId });
      }
      const { data } = await Document.uploadDocuments(
        formData.files?.[0],
        CARD_OBSERVATION_DOCUMENT_TYPE,
        UploadType.OBSERVATION,
        formData.explanation
      );
      if (data.result === 'OK') {
        setInnerDocument(data);
        setUploadedDocument(data);
        setHaveDocumentUploadedWarning(true);
      }
    } catch (error) {
      if (error && isInvalidFileError(error)) {
        setUploadError(error);
      }
    }
  };

  const handleActivateCard = async (): Promise<void> => {
    try {
      if (!allButtonsClicked) {
        setHaveDocumentUploadedWarning(false);
        setHaveClickedWarning(true);
      } else if (clickedButtons.includes(OBSERVATION_REQUIRED_DOCUMENT_TYPE) && !isPrinted) {
        setHaveDocumentUploadedWarning(false);
        setHavePrintedWarning(true);
      } else if (uploadedDocument) {
        const { data } = await PosActivation.activateCard({
          documentUploadData: uploadedDocument,
          cardActivationType: CardActivationType.OBSERVATION,
        });

        if (data.result === 'OK') {
          setIsApplicationCompleted(true);
          setIsCompleted && setIsCompleted(true);
        }
      }
    } catch (err) {
      // TODO (mertohancan): show error on modal or handle with error boundary
    }
  };

  return (
    <Container>
      {isApplicationCompleted ? (
        <Modal
          description={t('posActivation_sendDocument_successMessage')}
          onCancel={handleApplicationSuccessClick}
          submitText={t('general_components_close_button')}
          title={t('general_wellDone_text')}
          type={ModalType.Success}
        />
      ) : null}
      {isSuccessModalActive ? (
        <Modal
          description={t('cardObservation_digitalCard_successMessage')}
          onCancel={handleModalSuccessClick}
          submitText={t('general_components_close_button')}
          title={t('general_wellDone_text')}
          type={ModalType.Success}
        />
      ) : null}
      <Title>
        <Trans i18nKey="cardObservation_title" />
      </Title>

      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="general_cardNumber_label" />:
        </InfoLabel>
        {cardNumber}
      </InfoRow>
      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="cardObservation_applicationStatus_text" />:
        </InfoLabel>
        {cardStatus}
      </InfoRow>

      <Separator />
      <Subtitle>
        <Trans i18nKey="cardObservation_printDocument_text" />
      </Subtitle>

      <WarningContainer>
        <WarningIcon src={infoIcon} />
        <Subtitle>
          <Trans i18nKey="cardObservation_cardActivationNote_text" />
        </Subtitle>
      </WarningContainer>
      <Separator />
      {buttonList
        .filter(
          (button) =>
            button.observationDocumentType !== ObservationDocumentType.PRODUCTTRANSFER &&
            button.observationDocumentType !== ObservationDocumentType.DIGITALCARD &&
            button.observationDocumentType !== ObservationDocumentType.CARDACTIVATION
        )
        .map((button: ButtonProps) => (
          <ButtonListRow>
            <FormElement>
              <BigButton
                color={ButtonColor.Secondary}
                onClick={handleDownloadApplicationClick(button.observationDocumentType)}
                text={
                  button.observationDocumentType === ObservationDocumentType.DIGITALCARD ? (
                    t(button.buttonLabel)
                  ) : (
                    <SuccessButtonText
                      isSuccess={clickedButtons.includes(button.observationDocumentType)}
                      label={button.buttonLabel}
                    />
                  )
                }
                type="button"
              />
            </FormElement>
            {button.observationDocumentType === OBSERVATION_REQUIRED_DOCUMENT_TYPE &&
            clickedButtons.includes(OBSERVATION_REQUIRED_DOCUMENT_TYPE) ? (
              <CheckboxWrapper>
                <Checkbox
                  id="individualConditionsPrinted"
                  label={t('cardObservation_individualConditionsCheck_label')}
                  onChange={handlePrinted}
                  value={isPrinted}
                />
              </CheckboxWrapper>
            ) : null}
          </ButtonListRow>
        ))}
      {digitalCardButton.visible &&
      clickedButtons.includes(ObservationDocumentType.INDIVIDUALCONDITION) &&
      isPrinted ? (
        <FormElement>
          <BigButton
            color={ButtonColor.Primary}
            disabled={
              digitalCardButton.clickable === false ||
              clickedButtons.includes(ObservationDocumentType.DIGITALCARD)
            }
            onClick={handleDownloadApplicationClick(digitalCardButton.observationDocumentType)}
            text={t(digitalCardButton.buttonLabel)}
            type="button"
          />
        </FormElement>
      ) : null}
      {productTransferButton.visible &&
      clickedButtons.includes(ObservationDocumentType.INDIVIDUALCONDITION) &&
      isPrinted ? (
        <>
          <Separator />
          <ButtonListRow>
            <FormElement>
              <ProductTransferButton
                color={ButtonColor.Primary}
                disabled={
                  !productTransferButton.clickable ||
                  clickedButtons.includes(ObservationDocumentType.PRODUCTTRANSFER)
                }
                onClick={handleDownloadApplicationClick(
                  productTransferButton.observationDocumentType
                )}
                text={t(productTransferButton.buttonLabel)}
                type="button"
              />
            </FormElement>
          </ButtonListRow>
        </>
      ) : null}
      {!digitalCardButton.visible &&
      !productTransferButton.visible &&
      clickedButtons.includes(ObservationDocumentType.INDIVIDUALCONDITION) &&
      isPrinted ? (
        <>
          <ButtonListRow>
            <FormElement>
              <ProductTransferButton
                color={ButtonColor.Primary}
                onClick={singDocument()}
                text={t('cardObservation_productTransferChangeCardSubstatus_button')}
                type="button"
              />
            </FormElement>
          </ButtonListRow>
          <ButtonListRow>
            <FormElement>
              <BigButton
                color={ButtonColor.Secondary}
                onClick={handleDownloadApplicationClick(ObservationDocumentType.CARDACTIVATION)}
                text={
                  <SuccessButtonText
                    isSuccess={
                      clickedButtons.indexOf(ObservationDocumentType.CARDACTIVATION) !== -1
                    }
                    label="cardObservation_cardActivation_button"
                  />
                }
              />
            </FormElement>
          </ButtonListRow>
        </>
      ) : null}
      {!individualConditionsVisible && cardActivationButtonVisible ? (
        <ButtonListRow>
          <FormElement>
            <BigButton
              color={ButtonColor.Secondary}
              onClick={handleDownloadApplicationClick(ObservationDocumentType.CARDACTIVATION)}
              text={
                <SuccessButtonText
                  isSuccess={clickedButtons.indexOf(ObservationDocumentType.CARDACTIVATION) !== -1}
                  label="cardObservation_cardActivation_button"
                />
              }
            />
          </FormElement>
        </ButtonListRow>
      ) : null}
      {!digitalCardButton.visible &&
      productTransferButton.visible &&
      (clickedButtons.includes(ObservationDocumentType.PRODUCTTRANSFER) ||
        !productTransferButton.clickable) &&
      clickedButtons.includes(ObservationDocumentType.INDIVIDUALCONDITION) &&
      isPrinted ? (
        <ButtonListRow>
          <FormElement>
            <BigButton
              color={ButtonColor.Secondary}
              onClick={handleDownloadApplicationClick(ObservationDocumentType.CARDACTIVATION)}
              text={
                <SuccessButtonText
                  isSuccess={clickedButtons.indexOf(ObservationDocumentType.CARDACTIVATION) !== -1}
                  label="cardObservation_cardActivation_button"
                />
              }
            />
          </FormElement>
        </ButtonListRow>
      ) : null}
      {canUploadPanelVisible && showDocumentUploadForm ? (
        <UploadDocumentForm
          error={uploadError}
          handleContinue={handleActivateCard}
          setUploadedDocument={setUploadedDocument}
          submit={onSubmit}
          uploadedDocument={uploadedDocument}
        />
      ) : null}
      {haveDocumentUploadedWarning ? (
        <FormRow>
          <WarningText>
            <Trans i18nKey="cardObservation_afterUpload_errorMessage" />
          </WarningText>
        </FormRow>
      ) : null}
      {haveClickedWarning ? (
        <FormRow>
          <WarningText>
            <Trans i18nKey="general_documentsDownloadedControl_errorMessage" />
          </WarningText>
        </FormRow>
      ) : null}
      {havePrintedWarning ? (
        <FormRow>
          <WarningText>
            <Trans i18nKey="cardObservation_IndividualConditionsFormCheckBoxMustSelected _errorMessage" />
          </WarningText>
        </FormRow>
      ) : null}
    </Container>
  );
};

export default CardObservationActivation;
